/**
 * 호 hierarchy plain HTML table — 관세청 (HS Reference 2026) 식 column 구조.
 *
 * 자연스러운 페이지 flow — 그리드 자체 scroll 없음, 모든 row 가 한번에 노출.
 * 페이지 scroll 만 사용. (AUI Grid 의 fixed height + virtual scroll 패턴 폐기 — 사용자 image 210 의도)
 *
 * Columns:
 *   품목번호 (호 4 / 소호 2 / 세번 4 자리 split)
 *   품명 (한글 / 영문 grouped header)
 *   기본세율
 *   탄력·양허
 *
 * Row 유형:
 *  - 호 popum row (4-digit heading 본문 — 전체 popum)
 *  - 5-digit group row (예: 4202 1 — "트렁크·슈트 케이스...")
 *  - 6-digit subheading row (예: 4202 11 — "외부 표면을 가죽이나 ...")
 *  - 10-digit leaf row (예: 4202 11 1010 — "뱀의 것 / OF SNAKE / 8% / C 16% ...")
 *
 * 모든 row 의 호 column = yellow badge (관세청 식).
 *
 * Props:
 *  - hs4, heading_popum_ko, heading_popum_en
 *  - subheading_popums_ko, subheading_popums_en (backend 가 parse 한 6-digit popum map)
 *  - group_popums_ko, group_popums_en (5-digit group popum map)
 *  - rows: heading-detail rows (10-digit leaves)
 *  - onRowClick(hsk): leaf row 클릭 시 세번 진입
 *  - activeHsk: 선택된 세번 highlight
 */
import React, { useMemo, useEffect, useState } from "react";
import { categorizeRate } from "./rateCodeTaxonomy";
import SourceEvidenceLink, { tariffEvidenceUrl } from "./SourceEvidenceLink";
import {
  displayLegacyRequirements,
  structuredCustomsItems,
} from "./customsChecks";

// "42.02 - 트렁크·슈트 케이스..." → 앞의 "42.02 - " 제거
function stripLeadingDotCode(text) {
  if (!text) return "";
  return text.replace(/^[\d.]+\s*[-–]\s*/, "");
}

export default function HeadingGrid({
  hs4,
  heading_popum_ko,
  heading_popum_en,
  heading_title_ko,
  heading_title_en,
  subheading_popums_ko,
  subheading_popums_en,
  group_popums_ko,
  group_popums_en,
  rows,
  national_trees, // { "<hs6>": Node[]|null } — backend T3/T4 에서 주입
  onRowClick,
  activeHsk,
  hideHeadingPopum = false, // 소호 필터 시 호 popum(호 전체 설명) 행 숨김 — 그 소호만 표시
  matchTokens, // 검색 토큰 array — row label 매칭 시 노란 배경 highlight (2026-05-31)
  // compact=true: 한글-only narrow view. 세번 단계 좌측 patron 에서 재사용.
  // 영문 / 기본세율 / 탄력·양허 column 숨김. 호 popum 본문 wrap 표시.
  compact = false,
  isMobile = false, // 모바일 — 폰트·코드컬럼 폭 더 축소
}) {
  const [reqHsk, setReqHsk] = useState(null); // 요건·법령 팝업 대상 hsk (Phase 1)
  // 세관장확인 대상 세번(수입/수출) — Im/Ex 뱃지 붉은색(문의 #23). Spring GET /api/onto/req-customs?hs4=.
  const [customs, setCustoms] = useState({});
  useEffect(() => {
    if (!hs4 || !/^[0-9]{4}$/.test(String(hs4))) {
      setCustoms({});
      return undefined;
    }
    let alive = true;
    fetch(`/api/onto/req-customs?hs4=${hs4}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (alive) setCustoms((d && d.flags) || {});
      })
      .catch(() => {
        if (alive) setCustoms({});
      });
    return () => {
      alive = false;
    };
  }, [hs4]);
  const tokens = useMemo(() => {
    if (!Array.isArray(matchTokens)) return [];
    return matchTokens
      .map((t) => (typeof t === "string" ? t.toLowerCase().trim() : ""))
      .filter((t) => t.length >= 2); // 2자 미만 stop-word 방지
  }, [matchTokens]);
  const isRowMatched = (row) => {
    if (!tokens.length || row.type !== "leaf") return false;
    // flatRows 의 leaf row: ko = r.name_ko, en = r.popum
    const text = ((row.ko || "") + " " + (row.en || "")).toLowerCase();
    return tokens.every((t) => text.includes(t));
  };

  // 🆕 검색 매칭 leaf 가 정확히 1개일 때 자동 hsk 진입 (Step 3 — evidence panel
  //    바로 표시, 2026-05-31). 진입 후엔 activeHsk 있어 다시 fire 안 함.
  useEffect(() => {
    if (!tokens.length || !rows || activeHsk || !onRowClick) return;
    const matched = rows.filter((r) => {
      const text = ((r.name_ko || "") + " " + (r.popum || "")).toLowerCase();
      return tokens.every((t) => text.includes(t));
    });
    if (matched.length === 1) {
      onRowClick(matched[0].hsk);
    }
  }, [tokens, rows, activeHsk, onRowClick]);
  // hs6 별 그룹화 + sort
  const grouped = useMemo(() => {
    const byHs6 = new Map();
    for (const r of rows || []) {
      if (!byHs6.has(r.hs6)) byHs6.set(r.hs6, []);
      byHs6.get(r.hs6).push(r);
    }
    return Array.from(byHs6.entries())
      .map(([hs6, rs]) => ({
        hs6,
        rows: rs.sort((a, b) => a.hsk.localeCompare(b.hsk)),
      }))
      .sort((a, b) => a.hs6.localeCompare(b.hs6));
  }, [rows]);

  if (!rows || rows.length === 0) {
    return (
      <div
        style={{
          padding: 16,
          color: "var(--audit-text-muted)",
          fontSize: 12,
          fontStyle: "italic",
        }}
      >
        이 호에 대한 hierarchy 데이터 없음.
      </div>
    );
  }

  // 국내세분 트리 → flatRows 재귀 삽입 헬퍼.
  // hskMap: hsk → leaf row (rates/이름 포함), sub2: 6자리 소호 끝 2자리.
  // depth: 0 = 트리 루트 (7자리 prefix 그룹), 증가할수록 더 깊은 들여쓰기.
  function pushNationalNodes(nodes, hskMap, sub2, depth, target) {
    for (const node of nodes) {
      if (node.is_leaf) {
        // leaf: hskMap(master) 에서 rates 찾아 삽입. CLIP 리프인데 master 에 없어도
        //   skip 하지 않고 세율 빈값으로 렌더 → 공식 행개수 보존.
        const r = hskMap[node.hsk] || {};
        const tail4 = node.hsk.slice(6);
        const rates = r.rates || {};
        const basicRate = rates.A != null ? String(rates.A) : "";
        const tanParts = [];
        for (const [code, val] of Object.entries(rates)) {
          const info = categorizeRate(code);
          if (info.category === "basic" || info.isFta) continue;
          const display = val != null ? `${code} ${val}` : code;
          tanParts.push({ code, val, display });
        }
        const CAT_ORDER = [
          "wto",
          "apta",
          "quota_alloc",
          "quota_agri",
          "safeguard",
          "dumping",
          "adjustment",
          "intl_coop",
          "preference_ldc",
          "preference_nk",
          "preference_gsp",
          "wto_related",
          "misc",
        ];
        tanParts.sort((a, b) => {
          const ia = CAT_ORDER.indexOf(categorizeRate(a.code).category);
          const ib = CAT_ORDER.indexOf(categorizeRate(b.code).category);
          const oa = ia === -1 ? 99 : ia;
          const ob = ib === -1 ? 99 : ib;
          if (oa !== ob) return oa - ob;
          return a.code.localeCompare(b.code);
        });
        // CielHS 풍 컴팩트: 대표 1개 + 더 있으면 "…". 전체는 세번 클릭 시 상세.
        // ★대표 우선순위(2026-07-08, CL 패리티): 탄력 계열(할당 P·농림양허 W·긴급 T·
        //   덤핑 I·조정 L — 기본·양허를 대체하는 특수세율, 실무자가 목록에서 놓치면 안 됨)
        //   → APTA → 그 외(wto 등). 예: 2009.49 P1 20(할당)이 C 54 에 묻히던 것 해소.
        const ELASTIC = ["quota_alloc", "quota_agri", "safeguard", "dumping", "adjustment"];
        const _rep =
          tanParts.find((p) => ELASTIC.includes(categorizeRate(p.code).category)) ||
          tanParts.find((p) => categorizeRate(p.code).category === "apta") ||
          tanParts[0];
        const tanHot = !!(_rep && ELASTIC.includes(categorizeRate(_rep.code).category));
        const tan = _rep
          ? _rep.display + (tanParts.length > 1 ? " …" : "")
          : "";
        target.push({
          type: "leaf",
          key: node.hsk,
          hsk: node.hsk,
          sub2,
          tail4,
          ko: r.name_ko || node.name_ko || "",
          en: r.popum || node.name_en || "",
          basicRate,
          tan,
          tanHot, // 대표가 탄력 계열(할당 등) — 목록에서 강조
          has_import_req: r.has_import_req || false,
          has_export_req: r.has_export_req || false,
          _natDepth: depth, // 들여쓰기용 (leaf 는 국내세분 경로 안에서 렌더)
        });
      } else {
        // 그룹노드(CLIP 공식 군): 항상 헤더 행 렌더.
        //   CLIP 계층은 관세청 공식이라 단일-leaf 군도 법정 의미가 있으므로 생략 안 함.
        const children = node.children || [];
        const fmtPrefix = node.label || node.prefix || "";
        // 세번 칸에 그룹의 national 코드 표시 — 자식 leaf 들의 공통 national prefix.
        //   (예 0102.90.90 군: 9010·9020·9090 공통 "90" → "90 기타". 트리 prefix 가
        //    "0102909"(national "9")여도 실제 군 코드는 "90".) 관세청 원문과 일치.
        const _leafHsks = [];
        const _collect = (n) => {
          if (n.is_leaf) _leafHsks.push(n.hsk);
          else (n.children || []).forEach(_collect);
        };
        _collect(node);
        let groupTail = (node.prefix || "").slice(6);
        if (_leafHsks.length) {
          const tails = _leafHsks.map((h) => h.slice(6)); // 4자리 national tail
          let lcp = tails[0];
          for (const t of tails.slice(1)) {
            let i = 0;
            while (i < lcp.length && i < t.length && lcp[i] === t[i]) i++;
            lcp = lcp.slice(0, i);
          }
          if (lcp) groupTail = lcp;
        }
        target.push({
          type: "national_group",
          key: `__natg_${node.prefix}`,
          sub2,
          tail4: groupTail, // 그룹 national 코드 (예 "90")
          ko: fmtPrefix,
          en: node.name_en || "",
          basicRate: "",
          tan: "",
          _natDepth: depth,
        });
        if (children.length > 0) {
          pushNationalNodes(children, hskMap, sub2, depth + 1, target);
        }
      }
    }
  }

  // 모든 row flatten — 호 popum + 5d group + 6d subheading + 10d leaf
  const flatRows = [];

  // 호 popum row — 표제만 짧게 (전체 본문은 하단 호해설 탭 + 우 EvidencePanel).
  //   소호 필터(hideHeadingPopum) 시엔 호 전체 표제 숨김 — 선택 소호만 보여주기.
  const titleKo = heading_title_ko || stripLeadingDotCode(heading_popum_ko);
  const titleEn = heading_title_en || stripLeadingDotCode(heading_popum_en);
  if (!hideHeadingPopum && (titleKo || titleEn)) {
    flatRows.push({
      type: "heading",
      key: `__heading_${hs4}`,
      sub2: "",
      tail: "",
      ko: titleKo,
      en: titleEn,
      basicRate: "",
      tan: "",
    });
  }

  let prevHs5 = null;
  for (const { hs6, rows: hsks } of grouped) {
    const hs5 = hs6.slice(0, 5);
    const sub2 = hs6.slice(4);

    if (hs5 !== prevHs5) {
      const grKo = group_popums_ko?.[hs5] || "";
      const grEn = group_popums_en?.[hs5] || "";
      if (grKo || grEn) {
        flatRows.push({
          type: "group",
          key: `__group_${hs5}`,
          sub2: hs5.slice(4),
          tail: "",
          ko: grKo,
          en: grEn,
          basicRate: "",
          tan: "",
        });
      }
      prevHs5 = hs5;
    }

    const subKo = subheading_popums_ko?.[hs6] || "";
    const subEn = subheading_popums_en?.[hs6] || "";
    // single-leaf hs6 — subheading row 와 leaf row 가 사실상 중복 (예: 8201.10
    // = .10.00.00 단일) → subheading row skip. multi-leaf (예: 8201.90 → .1000
    // + .9000) 만 header 로서의 가치가 있으니 subheading row 유지.
    if ((subKo || subEn) && hsks.length > 1) {
      flatRows.push({
        type: "subheading",
        key: `__subheading_${hs6}`,
        sub2,
        tail: "",
        ko: subKo,
        en: subEn,
        basicRate: "",
        tan: "",
      });
    }

    // 국내세분 트리가 있으면 재귀 렌더, 없으면 기존 flat leaf rows 그대로
    const natTree = national_trees && national_trees[hs6];
    if (natTree && natTree.length > 0) {
      // hskMap 빌드 (rates/이름 재사용 위해)
      const hskMap = {};
      for (const r of hsks) hskMap[r.hsk] = r;
      pushNationalNodes(natTree, hskMap, sub2, 0, flatRows);
    } else {
      for (const r of hsks) {
        // 세번 specific 4자리 — 관세청 official 패턴 (한 cell `0000`)
        const tail4 = r.hsk.slice(6);
        const rates = r.rates || {};
        // 기본세율 column
        const basicRate = rates.A != null ? String(rates.A) : "";
        // 탄력·양허 column — 모든 일반(non-FTA, non-basic) 세율 코드를 "CODE VAL" 로 나열
        // (e.g. "C 13 · E1 6" for 4205001100). 값이 null(복합세율)인 경우 코드만 표시.
        const tanParts = [];
        for (const [code, val] of Object.entries(rates)) {
          const info = categorizeRate(code);
          if (info.category === "basic" || info.isFta) continue; // basic → 왼쪽 column. FTA → detail 패널.
          const display = val != null ? `${code} ${val}` : code;
          tanParts.push({ code, val, display });
        }
        // Sort by category order then code
        const CAT_ORDER = [
          "wto",
          "apta",
          "quota_alloc",
          "quota_agri",
          "safeguard",
          "dumping",
          "adjustment",
          "intl_coop",
          "preference_ldc",
          "preference_nk",
          "preference_gsp",
          "wto_related",
          "misc",
        ];
        tanParts.sort((a, b) => {
          const ia = CAT_ORDER.indexOf(categorizeRate(a.code).category);
          const ib = CAT_ORDER.indexOf(categorizeRate(b.code).category);
          const oa = ia === -1 ? 99 : ia;
          const ob = ib === -1 ? 99 : ib;
          if (oa !== ob) return oa - ob;
          return a.code.localeCompare(b.code);
        });
        // CielHS 풍 컴팩트: 대표 1개 + 더 있으면 "…". 전체는 세번 클릭 시 상세.
        // ★대표 우선순위(2026-07-08, CL 패리티): 탄력 계열(할당 P·농림양허 W·긴급 T·
        //   덤핑 I·조정 L — 기본·양허를 대체하는 특수세율, 실무자가 목록에서 놓치면 안 됨)
        //   → APTA → 그 외(wto 등). 예: 2009.49 P1 20(할당)이 C 54 에 묻히던 것 해소.
        const ELASTIC = ["quota_alloc", "quota_agri", "safeguard", "dumping", "adjustment"];
        const _rep =
          tanParts.find((p) => ELASTIC.includes(categorizeRate(p.code).category)) ||
          tanParts.find((p) => categorizeRate(p.code).category === "apta") ||
          tanParts[0];
        const tanHot = !!(_rep && ELASTIC.includes(categorizeRate(_rep.code).category));
        const tan = _rep
          ? _rep.display + (tanParts.length > 1 ? " …" : "")
          : "";
        flatRows.push({
          type: "leaf",
          key: r.hsk,
          hsk: r.hsk,
          sub2,
          tail4,
          ko: r.name_ko || "",
          en: r.popum || "",
          basicRate,
          tan,
          tanHot, // 대표가 탄력 계열(할당 등) — 목록에서 강조
          has_import_req: r.has_import_req || false,
          has_export_req: r.has_export_req || false,
        });
      }
    }
  }

  // 연속된 같은 sub2 중복 제거 (이미지 17 패턴) — group/subheading row 가
  // sub2 를 set 하면 그 다음 leaf 들의 같은 sub2 는 빈 칸. heading row 는
  // 대상 아님 (colSpan). 세번 4자리 (tail4) 는 한 cell 이라 중복 제거 X.
  let lastSub2 = null;
  for (const row of flatRows) {
    if (row.type === "heading") {
      lastSub2 = null;
      continue;
    }
    if (row.sub2 && row.sub2 === lastSub2) {
      row.displaySub2 = "";
    } else {
      row.displaySub2 = row.sub2;
      lastSub2 = row.sub2 || lastSub2;
    }
  }

  return (
    <>
    <table
      style={{
        width: "100%",
        fontSize: isMobile ? 10.5 : 11.5,
        borderCollapse: "collapse",
        background: "var(--audit-bg-elevated)",
        border: "1px solid var(--audit-border)",
        tableLayout: "fixed",
      }}
    >
      <colgroup>
        <col style={{ width: isMobile ? 32 : 40 }} />
        <col style={{ width: isMobile ? 22 : 28 }} />
        <col style={{ width: isMobile ? 38 : 44 }} />
        <col />
        {!compact && <col />}
        {!compact && <col style={{ width: 40 }} />}
        {!compact && <col style={{ width: 76 }} />}
        {!compact && <col style={{ width: 50 }} />}
        {!compact && <col style={{ width: 44 }} />}
      </colgroup>
      <thead>
        <tr style={{ background: "var(--audit-bg-subtle)" }}>
          <th style={thCenterStyle}>호</th>
          <th style={thCenterStyle}>소호</th>
          <th style={thCenterStyle}>세번</th>
          <th style={thCenterStyle}>{compact ? "HSK" : "한글"}</th>
          {!compact && <th style={thCenterStyle}>영문</th>}
          {!compact && <th style={thCenterStyle}>기본세율</th>}
          {!compact && <th style={thLeftStyle}>탄력·양허</th>}
          {!compact && <th style={thLeftStyle}>요건</th>}
          {!compact && <th style={thCenterStyle}>근거</th>}
        </tr>
      </thead>
      <tbody>
        {flatRows.map((row) => {
          const isLeaf = row.type === "leaf";
          const isActive = isLeaf && activeHsk && row.hsk === activeHsk;
          const isMatched = isLeaf && !isActive && isRowMatched(row);
          const rowBg =
            row.type === "heading"
              ? "var(--audit-bg-subtle)"
              : isActive
                ? "var(--audit-accent-bg)"
                : isMatched
                  ? "#fffbe6" // 검색 매칭 row — 연노랑 (2026-05-31)
                  : undefined;
          return (
            <tr
              key={row.key}
              onClick={() => {
                if (isLeaf && onRowClick) onRowClick(row.hsk);
              }}
              style={{
                cursor: isLeaf && onRowClick ? "pointer" : "default",
                background: rowBg,
              }}
              onMouseEnter={(e) => {
                if (isLeaf && onRowClick && !isActive)
                  e.currentTarget.style.background = "var(--audit-bg-hover)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = rowBg || "";
              }}
            >
              {row.type === "heading" ? (
                // 호 popum row — 호 cell 만 hs4 표시, 소호/세번 cell 비움
                // (이미지 26 처럼 7202 가 호 column 좌측 정렬)
                <>
                  <td style={{ ...hs4CellStyle, verticalAlign: "top" }}>
                    {hs4}
                  </td>
                  <td style={codeCellStyle}></td>
                  <td style={codeCellStyle}></td>
                </>
              ) : (
                <>
                  <td style={hs4CellStyle}></td>
                  <td style={codeCellStyle}>{row.displaySub2 || ""}</td>
                  <td style={codeCellStyle}>{row.tail4 || ""}</td>
                </>
              )}
              <td style={koCellStyle(row.type, row._natDepth)}>
                {isLeaf && (row.has_import_req || row.has_export_req) ? (
                  <span style={{ fontWeight: 700 }} title="요건대상 품목">{row.ko}</span>
                ) : (
                  row.ko
                )}
              </td>
              {!compact && <td style={enCellStyle(row.type)}>{row.en}</td>}
              {!compact && <td style={basicRateCellStyle}>{row.basicRate}</td>}
              {!compact && (
                <td
                  style={
                    row.tanHot
                      ? { ...tanRateCellStyle, color: "#b06000", fontWeight: 700 }
                      : tanRateCellStyle
                  }
                  title={row.tanHot ? "탄력세율(할당·양허 등) 시행 중 — 클릭해 상세 확인" : undefined}
                >
                  {row.tan}
                </td>
              )}
              {!compact && (
                <td style={reqCellStyle}>
                  {row.type === "leaf" &&
                  (row.has_import_req || row.has_export_req) ? (
                    <span
                      style={{
                        display: "flex",
                        gap: 3,
                        justifyContent: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      {row.has_import_req && (
                        <button
                          type="button"
                          style={{
                            ...reqBadgeType(customs[row.hsk]?.im),
                            ...reqBadgeBtnStyle,
                          }}
                          title={reqBadgeTitle("수입", customs[row.hsk]?.im)}
                          onClick={(e) => {
                            e.stopPropagation();
                            setReqHsk(row.hsk);
                          }}
                        >
                          Im
                        </button>
                      )}
                      {row.has_export_req && (
                        <button
                          type="button"
                          style={{
                            ...reqBadgeType(customs[row.hsk]?.ex),
                            ...reqBadgeBtnStyle,
                          }}
                          title={reqBadgeTitle("수출", customs[row.hsk]?.ex)}
                          onClick={(e) => {
                            e.stopPropagation();
                            setReqHsk(row.hsk);
                          }}
                        >
                          Ex
                        </button>
                      )}
                    </span>
                  ) : null}
                </td>
              )}
              {!compact && (
                <td style={{ ...reqCellStyle, textAlign: "center" }}>
                  <SourceEvidenceLink
                    compact
                    iconOnly
                    url={tariffEvidenceUrl(isLeaf ? row.hsk : row.code || hs4)}
                    label="관세청 원문"
                    title={`${isLeaf ? row.hsk : row.code || hs4} 관세율표 원문 조회`}
                  />
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
    {reqHsk && <ReqLawPopup hsk={reqHsk} onClose={() => setReqHsk(null)} />}
    </>
  );
}

const thCenterStyle = {
  textAlign: "center",
  padding: "6px 8px",
  borderBottom: "1px solid var(--audit-border-strong)",
  fontSize: 12,
  fontWeight: 600,
  color: "var(--audit-text-muted)",
};

// 세율 컬럼 헤더 — 값(rateCellStyle, 우측정렬)과 정렬 일치 (CielHS 풍 깔끔한 우측정렬)
// 탄력·양허 / 요건 헤더 — 값(왼쪽정렬)과 일치
const thLeftStyle = {
  ...thCenterStyle,
  textAlign: "left",
};

const hs4CellStyle = {
  padding: "3px 4px",
  textAlign: "center",
  verticalAlign: "top",
  background: "transparent",
  color: "var(--audit-text)",
  fontFamily: "JetBrains Mono, monospace",
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: "0.5px",
  borderRight: "1px solid var(--audit-border)",
};

const codeCellStyle = {
  padding: "3px 4px",
  textAlign: "center",
  verticalAlign: "top",
  fontFamily: "JetBrains Mono, monospace",
  fontWeight: 600,
  fontSize: 12,
  color: "var(--audit-text)",
  borderRight: "1px solid var(--audit-border)",
};

function koCellStyle(rowType, natDepth) {
  // national_group 들여쓰기: depth 0 = 8px, 1 = 18px, 2 = 28px, ...
  const padLeft =
    rowType === "national_group"
      ? 8 + (natDepth || 0) * 10
      : rowType === "leaf" && natDepth != null
        ? 8 + (natDepth + 1) * 10 // leaf 는 부모 그룹보다 한 단계 더 들여쓰기
        : rowType === "heading"
          ? 12
          : 10;
  return {
    padding: rowType === "heading" ? `10px ${padLeft}px` : `3px ${padLeft}px`,
    verticalAlign: "top",
    fontSize: rowType === "heading" ? 12.5 : 11.5,
    color: "var(--audit-text)",
    lineHeight: rowType === "heading" ? 1.7 : 1.5,
    whiteSpace: "normal",
    wordBreak: "keep-all",
    overflowWrap: "anywhere", // 긴 한글 chunk 가 cell 경계 넘어가지 않도록
    fontWeight:
      rowType === "group" ||
      rowType === "subheading" ||
      rowType === "national_group"
        ? 600
        : 400,
    borderRight: "1px solid var(--audit-border)",
  };
}

function enCellStyle(rowType) {
  return {
    padding: rowType === "heading" ? "10px 12px" : "3px 10px",
    verticalAlign: "top",
    fontSize: rowType === "heading" ? 12 : 11,
    color: "var(--audit-text-muted)",
    lineHeight: rowType === "heading" ? 1.65 : 1.45,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    borderRight: "1px solid var(--audit-border)",
  };
}

const rateCellStyle = {
  padding: "3px 8px",
  verticalAlign: "top",
  fontFamily: "JetBrains Mono, monospace",
  fontSize: 12,
  color: "var(--audit-text)",
  whiteSpace: "pre-line",
  borderRight: "1px solid var(--audit-border)",
};
// 기본세율 = 가운데 정렬, 탄력·양허 = 왼쪽 정렬 (사용자 지정)
const basicRateCellStyle = { ...rateCellStyle, textAlign: "center" };
const tanRateCellStyle = { ...rateCellStyle, textAlign: "left" };

const reqCellStyle = {
  padding: "3px 4px",
  verticalAlign: "top",
  textAlign: "left",
  borderRight: "1px solid var(--audit-border)",
};

// 요건 뱃지 — 배경 회색 통일, 유형은 글자 색으로(문의 #23): 세관장확인=빨강·통합공고=파랑·기타=회색.
//   방향(수입/수출)은 Im/Ex 글자로만 구분(배경·색으로 방향 구분 안 함 — 의미 없음).
const reqBadgeBase = {
  display: "inline-block",
  fontSize: 12,
  fontWeight: 700,
  lineHeight: "14px",
  padding: "0 3px",
  borderRadius: 2,
  background: "#eceef1",
  letterSpacing: "0.2px",
};
const REQ_TYPE_LABEL = { customs: "세관장확인", notice: "통합공고" };
function reqBadgeType(t) {
  return {
    ...reqBadgeBase,
    color: t === "customs" ? "#c0392b" : t === "notice" ? "#1d4ed8" : "#4b5563",
  };
}
function reqBadgeTitle(dir, t) {
  const kind = REQ_TYPE_LABEL[t];
  return `${dir}요건${kind ? "(" + kind + ")" : ""} — 클릭 시 법령·승인기관·적용기간`;
}

// 요건 뱃지를 클릭 가능한 버튼으로 (배지 스타일 위에 덮어씀)
const reqBadgeBtnStyle = {
  cursor: "pointer",
  border: "none",
  fontFamily: "inherit",
  padding: "1px 6px",
  fontSize: 11,
};

// ── 요건·법령 팝업: 요건 뱃지 클릭 시 그 HSK 의 requirements(법령·label·요건확인기관·전화) 표시.
//    데이터 = 기존 GET /api/onto/hsk-detail(requirements 포함) 재사용.
//    Phase 2(문의 #25): 백엔드 RequirementAgencyRef 가 [법령명]→소관부처·대표전화 매칭해
//    각 항목에 agencies 를 부착(참조=data/requirement_agency_ref.json, CLIP 세관장확인/통합공고 기준).
function fmtHsk(hsk) {
  const s = String(hsk || "");
  return s.replace(/^(\d{4})(\d{2})(\d{4})$/, "$1.$2-$3");
}

export function ReqLawPopup({ hsk, onClose }) {
  const [detail, setDetail] = useState(undefined); // undefined=로딩, null=없음
  useEffect(() => {
    let alive = true;
    fetch(`/api/onto/hsk-detail?hsk=${encodeURIComponent(hsk)}`)
      .then((r) => r.json())
      .then((d) => {
        if (alive) setDetail(d || null);
      })
      .catch(() => {
        if (alive) setDetail(null);
      });
    return () => {
      alive = false;
    };
  }, [hsk]);

  const req = displayLegacyRequirements(detail);
  const customsItems = structuredCustomsItems(detail);
  const groups = [];
  if (customsItems.length) {
    groups.push([
      "세관장확인",
      customsItems.map((item) => ({
        label: `세관장확인 · ${item.status_label}`,
        text: `[${item.related_law}]\n${item.text}`,
        period: `${item.apply_start_date} ~ ${item.apply_end_date || "종료일 없음"}`,
        observed: item.observed_in_active_snapshot,
        officialAgencies: item.agencies || [],
        status: item.status,
      })),
    ]);
  }
  if (req) {
    if (Array.isArray(req.import) && req.import.length)
      groups.push(["수입", req.import]);
    if (Array.isArray(req.export) && req.export.length)
      groups.push(["수출", req.export]);
    if (Array.isArray(req.strategic) && req.strategic.length)
      groups.push(["전략물자", req.strategic.map((s) => ({ label: "", text: s }))]);
  }

  return (
    <div onClick={onClose} style={reqOverlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={reqBoxStyle}>
        <div style={reqHeadStyle}>
          <span>요건 · 법령 — {fmtHsk(hsk)}</span>
          <button type="button" onClick={onClose} style={reqCloseStyle}>
            ✕
          </button>
        </div>
        <div style={reqBodyStyle}>
          {detail === undefined ? (
            <div style={{ color: "#888", fontSize: 13 }}>불러오는 중…</div>
          ) : groups.length === 0 ? (
            <div style={{ color: "#888", fontSize: 13 }}>등록된 요건이 없습니다.</div>
          ) : (
            groups.map(([heading, items]) => (
              <div key={heading} style={{ marginBottom: 12 }}>
                <div style={reqGroupHeadStyle}>{heading}요건</div>
                {items.map((it, i) => (
                  <div key={i} style={reqItemStyle}>
                    {it.label ? (
                      <span
                        style={{ ...reqLabelStyle, color: reqTypeFontColor(it.label) }}
                      >
                        {it.label}
                      </span>
                    ) : null}
                    <span style={{ whiteSpace: "pre-wrap" }}>{it.text}</span>
                    {it.period && (
                      <div style={{ marginTop: 3, fontSize: 11, color: "#6b7280" }}>
                        적용기간 {it.period}
                        {it.observed === false ? " · 최신 목록 미관측(검증 이력 보존)" : ""}
                      </div>
                    )}
                    {Array.isArray(it.officialAgencies) && it.officialAgencies.length > 0 && (
                      <div style={reqAgencyWrapStyle}>
                        {it.officialAgencies.map((agency) => (
                          <div key={agency.name} style={reqAgencyRowStyle}>
                            <span style={reqAgencyTagStyle}>
                              요건승인기관
                            </span>
                            <span style={reqAgencyOrgStyle}>{agency.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {Array.isArray(it.agencies) && it.agencies.length > 0 && (
                      <div style={reqAgencyWrapStyle}>
                        {it.agencies.map((a, j) => (
                          <div key={j} style={reqAgencyRowStyle}>
                            <span style={reqAgencyTagStyle}>소관부처 문의처</span>
                            <span style={reqAgencyOrgStyle}>{a.ministry}</span>
                            {a.tel ? (
                              <a
                                href={`tel:${String(a.tel).replace(/[^0-9]/g, "")}`}
                                style={reqAgencyTelStyle}
                              >
                                {a.tel}
                              </a>
                            ) : null}
                            {a.law ? <span style={reqAgencyLawStyle}>{a.law}</span> : null}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))
          )}
          {groups.length > 0 && (
            <div style={{ display: "flex", gap: 12, marginTop: 10, borderTop: "1px solid #eee", paddingTop: 8, fontSize: 11, color: "#6b7280", flexWrap: "wrap" }}>
              <span>
                <b style={{ color: "#c0392b" }}>적용 중 세관장확인</b> 통관 필수확인
              </span>
              <span>
                <b style={{ color: "#1d4ed8" }}>통합공고</b>
              </span>
              <span>Im 수입 · Ex 수출</span>
            </div>
          )}
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 8, paddingTop: 4 }}>
            {req && req.agency_footer
              ? `※ ${req.agency_footer}`
              : "※ 소관부처 문의처는 대표번호 기준입니다. 정부민원안내 국번없이 110."}
          </div>
        </div>
      </div>
    </div>
  );
}

const reqOverlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 4000,
  padding: 16,
};
const reqBoxStyle = {
  background: "#fff",
  borderRadius: 12,
  width: "min(680px, 96vw)",
  maxHeight: "82vh",
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
  fontFamily: "'Malgun Gothic', '맑은 고딕', 'Pretendard', -apple-system, 'Segoe UI', sans-serif",
};
const reqHeadStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 18px",
  borderBottom: "1px solid #ececf2",
  fontSize: 15,
  fontWeight: 800,
  color: "#16151a",
};
const reqCloseStyle = {
  border: "none",
  background: "transparent",
  fontSize: 18,
  cursor: "pointer",
  color: "#6b7280",
  lineHeight: 1,
};
const reqBodyStyle = { padding: "16px 18px", overflow: "auto" };
const reqGroupHeadStyle = {
  fontSize: 13,
  fontWeight: 800,
  color: "#374151", // 수입/수출 방향 색 구분 제거(의미 없음 — Im/Ex로 충분), 중립 회색
  marginBottom: 6,
};
const reqItemStyle = {
  fontSize: 13,
  color: "#374151",
  lineHeight: 1.7,
  padding: "6px 0",
  borderBottom: "1px dashed #f0f0f4",
};
// 요건 라벨 = 폰트 색으로 유형 구분(문의 #23): 세관장확인=붉은색·통합공고=청록·기타=회색.
const reqLabelStyle = {
  display: "inline-block",
  fontSize: 11.5,
  fontWeight: 700,
  marginRight: 8,
  verticalAlign: "top",
};
function reqTypeFontColor(label) {
  if (String(label).startsWith("세관장확인")) return "#c0392b"; // 빨강
  if (label === "통합공고") return "#1d4ed8"; // 파랑
  return "var(--audit-text-muted, #5f6368)";
}
// ── 요건확인기관(소관부처)·전화 (Phase 2) ──
const reqAgencyWrapStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  marginTop: 6,
};
const reqAgencyRowStyle = {
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 6,
  fontSize: 12,
};
const reqAgencyTagStyle = {
  fontSize: 10,
  fontWeight: 700,
  background: "#f3f4f6", // 파랑 절제·초록 금지 — 회색 표식(2026-10-02)
  color: "#374151",
  border: "1px solid #e5e7eb",
  borderRadius: 4,
  padding: "1px 6px",
};
const reqAgencyOrgStyle = { fontWeight: 700, color: "#111827" };
const reqAgencyTelStyle = {
  fontWeight: 700,
  color: "#111827",
  textDecoration: "underline dotted",
  textUnderlineOffset: 2,
  fontVariantNumeric: "tabular-nums",
};
const reqAgencyLawStyle = { color: "#9ca3af", fontSize: 11 };
